import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { Scrim, Modal, Btn, IconBtn, Field, Input, Select, FormGrid } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import FieldHelp from '../ui/FieldHelp';
import { ENROLLMENT_FIELD_HELP as HELP } from './enrollmentFieldHelp';
import { useCourseList } from '../hooks/useCourseList';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* Compact form shell — a learner form doesn't need the full 720px modal width. */
const FormModal = styled(Modal)`
  max-width: 460px;

  .modal__body { padding: 22px 24px; }
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

/* Primary action mirrors ConfirmDialog: readable disabled state, no hover lift. */
const SubmitBtn = styled(Btn)`
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export default function FormDialog({ open, handleClose, data, onChange, handleFormSubmit, loading, lockCourse = false }) {
    const { firstName, lastName, studentEmail } = data;
    const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();

    // Shared cached course list — no per-render network call.
    const { courses, coursesLoading } = useCourseList();

    const isEdit = !!data.id;

    // The select is keyed by courseId (its value IS the courseId), so the id is
    // always captured directly — no name lookup / update-timing races. courseName
    // is set alongside it for display and the backend payload.
    const handleCourseChange = (e) => {
        const selectedCourseId = e.target.value;
        const selectedCourse = courses.find(c => String(c.id) === String(selectedCourseId));
        onChange({ target: { id: 'courseId', value: selectedCourseId } });
        onChange({ target: { id: 'courseName', value: selectedCourse ? selectedCourse.name : '' } });
    };

    const handleSubmit = async () => {
        if (loading) return;
        if (!guardAction('action.enrollment.save')) return;
        try {
            await handleFormSubmit(); // Call the handleFormSubmit function
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    // Escape-to-close + body scroll lock while the modal is open — matches the
    // established ConfirmDialog behaviour.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => { if (event.key === 'Escape') handleClose(); };
        window.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, handleClose]);

    const handleScrimClick = (event) => {
        if (event.target === event.currentTarget) handleClose();
    };

    return (
        <>
            {open && createPortal(
                <Scrim onClick={handleScrimClick} role="presentation">
                    <FormModal
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="student-dialog-title"
                    >
                        <div className="modal__head">
                            <div>
                                <h3 id="student-dialog-title">{isEdit ? 'Update user' : 'Create new user'}</h3>
                                <p>{isEdit ? 'Update this learner’s details.' : 'Add a learner and enroll them in a course.'}</p>
                            </div>
                            <IconBtn
                                className="x-btn"
                                type="button"
                                onClick={handleClose}
                                aria-label="Close dialog"
                            >
                                <X size={18} />
                            </IconBtn>
                        </div>

                        <form
                            className="modal__body"
                            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                        >
                            <FormGrid>
                                <Field>
                                    <label htmlFor="firstName">First name</label>
                                    <Input
                                        id="firstName"
                                        value={firstName || ''}
                                        onChange={onChange}
                                        placeholder="Jane"
                                        autoComplete="given-name"
                                    />
                                </Field>
                                <Field>
                                    <label htmlFor="lastName">Last name</label>
                                    <Input
                                        id="lastName"
                                        value={lastName || ''}
                                        onChange={onChange}
                                        placeholder="Doe"
                                        autoComplete="family-name"
                                    />
                                </Field>
                            </FormGrid>

                            <Field>
                                <FieldLabel htmlFor="studentEmail">
                                    Email
                                    <FieldHelp text={HELP.email} />
                                </FieldLabel>
                                <Input
                                    id="studentEmail"
                                    type="email"
                                    value={studentEmail || ''}
                                    onChange={onChange}
                                    placeholder="jane.doe@example.com"
                                    autoComplete="email"
                                />
                            </Field>

                            <Field style={{ marginBottom: 0 }}>
                                <FieldLabel htmlFor="courseId">
                                    Course
                                    <FieldHelp text={HELP.courseName} />
                                </FieldLabel>
                                <Select
                                    id="courseId"
                                    value={data.courseId || ''}
                                    onChange={handleCourseChange}
                                    disabled={lockCourse || coursesLoading}
                                >
                                    <option value="">{coursesLoading ? 'Loading courses…' : 'Select a course'}</option>
                                    {courses.map((course) => (
                                        <option key={course.id} value={course.id}>
                                            {course.name}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                        </form>

                        <div className="modal__foot">
                            <Btn type="button" $variant="ghost" onClick={handleClose}>Cancel</Btn>
                            <SubmitBtn
                                type="button"
                                $variant="subtle"
                                onClick={handleSubmit}
                                disabled={loading}
                                aria-disabled={loading}
                            >
                                {loading ? <ButtonSpinnerSlot size={16} /> : (isEdit ? 'Save changes' : 'Create user')}
                            </SubmitBtn>
                        </div>
                    </FormModal>
                </Scrim>,
                document.body,
            )}
            <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
        </>
    );
}
